/**
 * Sign in with Google (ADR 0023).
 *
 * The button is a plain link to the Platform API, not a fetch: signing in with Google is a round
 * trip through Google's pages, so the browser has to navigate. The API answers with a redirect to
 * Google, Google redirects back to the API, and the API redirects here -- to the app when it worked,
 * or to `/login?oauth_error=<code>` (`/settings` when connecting) when it did not.
 */
import { useAuthProviders, type SignInProvider } from '../lib/api';
import { buttonClasses } from './ui';

/**
 * Where to send the browser to begin.
 *
 * `redirect` is where to land afterwards. Only an in-app path is passed on; the API checks it again,
 * because this is a query string anyone can write.
 */
export function oauthStartHref(
  provider: SignInProvider,
  options: { intent?: 'link'; redirect?: string | undefined } = {},
): string {
  const params = new URLSearchParams();
  if (options.intent !== undefined) params.set('intent', options.intent);
  const redirect = options.redirect;
  if (redirect?.startsWith('/') === true && !redirect.startsWith('//')) {
    params.set('redirect', redirect);
  }
  const query = params.toString();
  return `/api/auth/oauth/${provider}/start${query === '' ? '' : `?${query}`}`;
}

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  cancelled: 'Signing in with Google was cancelled.',
  failed: 'Signing in with Google did not work. Please try again.',
  unavailable: 'Signing in with Google is not available right now. Please try again later.',
  unverified_email:
    'Google has not verified the email address on that Google account, so it cannot be used here.',
  account_exists:
    'An account already uses this email address. Sign in with your password, then connect Google in Settings.',
  already_linked: 'That Google account is already connected to a different urlshot.io account.',
};

/** The message for an `oauth_error` code, or null when there is none or it is unknown. */
export function oauthErrorMessage(code: unknown): string | null {
  return typeof code === 'string' ? (OAUTH_ERROR_MESSAGES[code] ?? null) : null;
}

/** Google's "G", as its sign-in branding guidelines ask buttons to show it. */
export function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/**
 * "Continue with Google", above the email form, with an "or" between them.
 *
 * Renders nothing when this environment has no Google credentials, rather than a button that can
 * only fail. The same button signs in and signs up: which one happens is the API's decision.
 */
export function ProviderSignIn({ redirect }: { redirect?: string | undefined }) {
  const { data: providers } = useAuthProviders();
  if (providers?.includes('google') !== true) return null;

  return (
    <div className="flex flex-col gap-4">
      <a
        href={oauthStartHref('google', { redirect })}
        className={buttonClasses('secondary', 'w-full')}
        data-testid="continue-with-google"
      >
        <GoogleMark />
        Continue with Google
      </a>
      <div
        className="flex items-center gap-3 text-xs uppercase tracking-wide text-neutral-500"
        aria-hidden="true"
      >
        <span className="h-px flex-1 bg-neutral-200" />
        or
        <span className="h-px flex-1 bg-neutral-200" />
      </div>
    </div>
  );
}
